import { extent, type Numeric } from "d3";
import type {
	AxisRange,
	AxisSide,
	CartesianSerieOptions,
	DataPoint,
	XAxisSide,
	YAxisSide,
} from "../interfaces";

export type ScalesRangeMap = Record<AxisSide, AxisRange>;

export const useCartesianSerieRange = () => {
	const seriesExtent = (
		series: DataPoint[][],
		accessor: (datum: DataPoint) => Numeric | null,
		padding?: number,
		isLog?: boolean
	) => {
		let allMinMax: (Numeric | null)[] = [];

		for (const serie of series) {
			allMinMax = allMinMax.concat(extent(serie, accessor) as [Numeric | null, Numeric | null]);
		}

		const minMax = extent(
			allMinMax.filter((n): n is Numeric => n != null && isFinite(n.valueOf()))
		);
		const min = minMax[0];
		const max = minMax[1];

		if (typeof min === "number" && typeof max === "number") {
			if (isLog && min > 0 && max > 0) {
				const logMin = Math.log(min);
				const logMax = Math.log(max);
				const logDistance = padding ?? (logMax - logMin) * 0.05;

				return {
					min: Math.exp(logMin - logDistance),
					max: Math.exp(logMax + logDistance),
				};
			}

			let distance: number;

			if (padding != null) {
				distance = padding;
			} else {
				const diff = max - min;
				if (diff !== 0) {
					// Add 5% offset of the difference betwen min and max
					distance = diff * 0.05;
				} else if (max !== 0) {
					// Add 10% of the max value, since it's the same as the min value
					distance = Math.abs(max) * 0.05;
				} else {
					distance = 1;
				}
			}
			return { min: min - distance, max: max + distance };
		}

		return { min, max };
	};

	const xRange = (
		series: CartesianSerieOptions[],
		side: XAxisSide,
		padding?: number,
		isLog?: boolean
	) => {
		const filteredSeries = series.filter((s) => s.xAxis === side);

		const seriesDataPoint = filteredSeries.map((serie) => serie.data);
		return seriesExtent(
			seriesDataPoint,
			(d) => (isFinite(d.y?.valueOf() ?? NaN) ? d.x : null),
			padding,
			isLog
		);
	};

	const yRange = (
		series: CartesianSerieOptions[],
		side: YAxisSide,
		padding?: number,
		isLog?: boolean
	) => {
		const filteredSeries = series.filter((s) => s.yAxis === side);

		const seriesDataPoint = filteredSeries.map((serie) => serie.data);
		return seriesExtent(
			seriesDataPoint,
			(d) => (isFinite(d.y?.valueOf() ?? NaN) ? d.y : null),
			padding,
			isLog
		);
	};

	return { xRange, yRange };
};
